import { isAxiosError } from 'axios'
import { toast } from 'react-toastify'

import httpClient from '../../httpClient.ts'
import {
  FetchTransactionsResponse,
  FetchTransactionsResponseData,
  StripeAccountData,
  StripeBalanceObject,
  StripeCreateOnboardLink
} from './types.ts'

const retrieve_stripe_balance = async () => {
  try {
    const response = await httpClient.get<StripeBalanceObject>(
      '/api/stripe/retrieve_stripe_balance/'
    )
    return response.data
  } catch (error) {
    if (isAxiosError(error)) {
    }
    throw new Error('An unexpected error occurred')
  }
}

const create_payout = async (data: { amount: number; currency: string }) => {
  try {
    const response = await httpClient.post('/api/stripe/create_payout/', data)
    return response.data
  } catch (error) {
    if (isAxiosError(error)) {
      const cleanedString = error.response?.data.error.replace(/^\["|"]$/g, '')
      toast.error(cleanedString)
    }
    throw new Error('An unexpected error occurred')
  }
}

const createExpressAccount = async () => {
  let fetchResponse = {
    data: {},
    status: 200,
    error: null
  }
  try {
    const response = await httpClient.post('/api/stripe/onboard_user/')
    fetchResponse.data = response.data
    fetchResponse.status = response.status
  } catch (error) {
    if (isAxiosError(error)) {
      fetchResponse.error = error.response?.data.error_code
      fetchResponse.status = error.response?.status as number
    }
  }
  return fetchResponse
}

const getOnboardLink = async () => {
  let fetchResponse = {
    data: {} as StripeCreateOnboardLink<Record<string, undefined>>,
    status: 200,
    error: null
  }

  try {
    const response = await httpClient.get('/api/stripe/get_onboard_link/')
    fetchResponse.data = response.data
    fetchResponse.status = response.status
  } catch (error) {
    if (isAxiosError(error)) {
      fetchResponse.error = error.response?.data.error_code
      fetchResponse.status = error.response?.status as number
    }
  }
  return fetchResponse
}

const mergeParamsIntoUrl = (
  baseUrl: string,
  params: Record<string, string | undefined>
): string => {
  const queryString = Object.entries(params)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) => value !== undefined && value !== '') // Remove params with undefined or empty values
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`) // Encode value for safety
    .join('&') // Concatenate with "&"

  return queryString ? `${baseUrl}?${queryString}` : baseUrl // Add query string if not empty
}

const getStripeTransactions = async (
  starting_after: string | undefined = '',
  objects_type?: 'payouts' | 'charges'
) => {
  let fetchResponse: FetchTransactionsResponse = {
    data: {} as FetchTransactionsResponseData,
    status: 200,
    error: null
  }

  try {
    const baseUrl = '/api/stripe/retrieve_stripe_transactions/'
    const fullUrl = mergeParamsIntoUrl(baseUrl, {
      starting_after,
      objects_type
    })
    const response = await httpClient.get(fullUrl)
    fetchResponse.data = response.data
    fetchResponse.status = response.status
  } catch (error) {
    if (isAxiosError(error)) {
      fetchResponse.error = error.response?.data.error_code
      fetchResponse.status = error.response?.status as number
    }
  }
  return fetchResponse
}

const getOrderByTransferId = async (stripe_transfer_id: string) => {
  try {
    const url = `/api/dd_panel_orders/retrieve_by_transfer_id/${stripe_transfer_id}/`
    const response = await httpClient.get(url)
    return response.data
  } catch (error) {
    if (isAxiosError(error)) {
      const cleanedString = error.response?.data.detail
      toast.error(cleanedString)
    }
    throw new Error('An unexpected error occurred')
  }
}

const getOrderByPaymentIntentId = async (stripe_payment_intent_id: string) => {
  try {
    const url = `/api/dd_panel_orders/retrieve_by_stripe_payment_intent_id/${stripe_payment_intent_id}/`
    const response = await httpClient.get(url)
    return response.data
  } catch (error) {
    if (isAxiosError(error)) {
      const cleanedString = error.response?.data.detail.replace(/^\["|"]$/g, '')
      toast.error(cleanedString)
    }
    throw new Error('An unexpected error occurred')
  }
}

const getStripeData = async () => {
  let fetchResponse = {
    data: {} as StripeAccountData,
    status: 200,
    error: null
  }
  try {
    const response = await httpClient.get('/api/users/get_stripe_data/')
    fetchResponse.data = response.data
    fetchResponse.status = response.status
  } catch (error) {
    if (isAxiosError(error)) {
      fetchResponse.error = error.response?.data.error_code
      fetchResponse.status = error.response?.status as number
    }
  }
  return fetchResponse
}

const requirements_filled = async () => {
  let fetchResponse = {
    data: {} as StripeAccountData,
    status: 200,
    error: null
  }
  try {
    const response = await httpClient.get('/api/stripe/requirements_filled/')
    fetchResponse.data = response.data
    fetchResponse.status = response.status
  } catch (error) {
    if (isAxiosError(error)) {
      fetchResponse.error = error.response?.data.error_code
      fetchResponse.status = error.response?.status as number
    }
  }
  return fetchResponse
}

export const stripeAPI = {
  retrieve_stripe_balance,
  create_payout,
  createExpressAccount,
  getOnboardLink,
  getStripeTransactions,
  getOrderByTransferId,
  getOrderByPaymentIntentId,
  getStripeData,
  requirements_filled,
}
